import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  login as loginService,
  signup as signupService,
  forgotpassword as forgotpassService,
  resetpassword as resetpassService,
  AuthResponse,
} from "@/services/authApi";
import { getUserDetails } from "@/services/userApi";

interface User {
  id: string;
  email: string;
  f_name?: string;
  l_name?: string;
  unique_id?: string;
  profile_image?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    email: string,
    password: string,
    remember_me: boolean,
  ) => Promise<AuthResponse>;
  signup: (
    f_name: string,
    l_name: string,
    email: string,
    password: string,
    consent: boolean,
  ) => Promise<AuthResponse>;
  forgotpassword: (email: string) => Promise<AuthResponse>;
  resetpassword: (
    token: string,
    confirm_password: string,
  ) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  getCurrentUser: () => Promise<User | null>;
  getUserdata: () => Promise<User | null>;
    fetchUser: (id: string) => Promise<void>;
  refreshUser: () => Promise<void>; // ✅ NEW
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * ✅ Safely decode JWT and extract user info
   */
  const decodeToken = (token: string): User | null => {
    try {
      const base64Payload = token.split(".")[1];
      if (!base64Payload) return null;

      const payload = JSON.parse(atob(base64Payload));

      // Handle multiple backend key naming conventions
      const id =
        payload.id || payload.userId || payload.sub || payload._id || null;

      if (!id) return null;

      return {
        id,
        email: payload.email || "",
        f_name: payload.f_name || payload.first_name || "",
        l_name: payload.l_name || payload.last_name || "",
        unique_id: payload.unique_id || "",
        profile_image: payload?.profile_image,
      };
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  };

  /**
   * ✅ Get current user from localStorage
   */
  // const getCurrentUser = async (): Promise<User | null> => {

  //   const token = localStorage.getItem("auth_token");
  //   if (!token) {
  //     setUser(null);
  //     return null;
  //   }

  //   const decodedUser = decodeToken(token);

  //   setUser(decodedUser);
  //   return decodedUser;
  // };
  const getCurrentUser = async (): Promise<User | null> => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      setUser(null);
      return null;
    }

    const decodedUser = decodeToken(token);
    if (!decodedUser?.id) return null;

    try {
      const response = await getUserDetails(decodedUser.id);
      const profile = response.value;

      const freshUser: User = {
        id: profile._id,
        email: profile.email,
        f_name: profile.f_name,
        l_name: profile.l_name,
        unique_id: profile.unique_id,
        profile_image: profile.profile_image,
      };

      setUser(freshUser);
      return freshUser;
    } catch (err) {
      console.error("Failed to fetch user profile", err);
      setUser(decodedUser); // fallback
      return decodedUser;
    }
  };

  const getUserdata = async (): Promise<User | null> => {
    return new Promise((resolve) => {
      const userStr = localStorage.getItem("user");
      resolve(userStr ? JSON.parse(userStr) : null);
    });
  };

  const fetchUser = async (id: string) => {
    try {
      const data = await getUserDetails(id); // ✅ API call
      const u = data.value;

      setUser({
        id: u._id,
        email: u.email,
        f_name: u.f_name,
        l_name: u.l_name,
        profile_image: u.profile_image, 
      });
    } catch (err) {
      console.error("❌ Failed to load user", err);
      setUser(null);
      localStorage.removeItem("auth_token");
    }
  };

  /**
   * ✅ Load user on first mount
   */
  useEffect(() => {
    (async () => {
      await getCurrentUser();
      setIsLoading(false);
    })();
  }, []);

  const refreshUser = async () => {
    if (!user?.id) return;
    await getCurrentUser();
  };

  /**
   * ✅ Auth methods
   */
  const login = async (
    email: string,
    password: string,
    remember_me: boolean = false,
  ): Promise<AuthResponse> => {
    setIsLoading(true);

    try {
      const data = await loginService({ email, password, remember_me });

      if (!data["value"].mfa_required) {
        const userData = data["value"];

        setUser({
          id: userData._id || userData.id,
          email: userData.email,
          f_name: userData.f_name,
          l_name: userData.l_name,
          unique_id: userData.unique_id,
        });
      }

      return data;
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (
    f_name: string,
    l_name: string,
    email: string,
    password: string,
    consent: boolean,
  ): Promise<AuthResponse> => {
    setIsLoading(true);
    try {
      const data = await signupService({
        f_name,
        l_name,
        email,
        password,
        consent,
      });
      const userData = data["value"];
      setUser({
        id: userData.id,
        email,
        f_name: userData.f_name,
        l_name: userData.l_name,
        unique_id: userData.unique_id,
      });
      return data;
    } finally {
      setIsLoading(false);
    }
  };

  const forgotpassword = async (email: string): Promise<AuthResponse> => {
    setIsLoading(true);
    try {
      return await forgotpassService({ email });
    } finally {
      setIsLoading(false);
    }
  };

  const resetpassword = async (
    token: string,
    new_password: string,
  ): Promise<AuthResponse> => {
    setIsLoading(true);
    try {
      return await resetpassService({ token, new_password });
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setIsLoading(true);
    try {
      localStorage.removeItem("auth_token");
      localStorage.clear();
      sessionStorage.clear();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        signup,
        forgotpassword,
        resetpassword,
        logout,
        getCurrentUser,
        getUserdata,
        refreshUser,
        fetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
